// AiShell (DESIGN §3.3.1, §3.3.3): AiSidebar links (280, eingeklappt 64) → Spalte mit AiTopBar 56
// → main (Lesespalte 760 bzw. 1200). Mobil: Seitenleiste als Drawer, Topbar mit Menü, Titel,
// „New chat“. ⌘⇧S klappt die Seitenleiste; der Zustand wird lokal gemerkt.
import { List, PencilSimpleLine, SidebarSimple } from '@phosphor-icons/react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { cn } from '../kern/cn';
import { SkipLink } from '../kern/Misc';
import { PlatformMark } from '../kern/Monogram';
import { PageChromeProvider } from '../kern/page-chrome';
import { Drawer, ShellIconButton, TooltipProvider, useMediaQuery } from './parts';

const COLLAPSE_KEY = 'bar.ai.sidebar';

export interface AiSidebarContext {
  collapsed: boolean;
  toggle?: () => void;
  /** Nach einer Wahl im Drawer schließen. */
  onNavigate?: () => void;
}

export interface AiShellProps {
  sidebar: (ctx: AiSidebarContext) => ReactNode;
  /** Titel des Gesprächs (sonst Seitentitel bzw. „Bar AI“). */
  title?: ReactNode;
  /** Rechts in der Topbar (Menü „…“ des Gesprächs, Status-Chip). */
  actions?: ReactNode;
  /** Nur mobil rechts: Avatar mit AccountMenu. */
  mobileAccount?: ReactNode;
  newChatHref: string;
  /** `wide` für Knowledge-Seiten (1200). */
  width?: 'read' | 'wide';
  navKey?: string;
  overlays?: ReactNode;
  children: ReactNode;
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

/** Schlanke Topbar 56: Gesprächstitel (Seiten mit PageHeader zeigen ihren Titel im Inhalt). */
function AiTopBar({
  title,
  leading,
  actions,
}: {
  title?: ReactNode;
  leading?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="sticky top-0 z-(--bar-z-nav) flex h-topbar-m shrink-0 items-center gap-1 bg-page px-2 md:px-4">
      {leading}
      <div className="min-w-0 flex-1 truncate px-2 text-button text-fg">{title}</div>
      {actions}
    </header>
  );
}

export function AiShell({
  sidebar,
  title,
  actions,
  mobileAccount,
  newChatHref,
  width = 'read',
  navKey,
  overlays,
  children,
}: AiShellProps) {
  const isMd = useMediaQuery('(min-width: 768px)');
  const isLg = useMediaQuery('(min-width: 1024px)');
  const [collapsedPref, setCollapsedPref] = useState(readCollapsed);
  // Der Drawer merkt sich den Pfad, auf dem er geöffnet wurde; jede Navigation schließt ihn.
  const [openAt, setOpenAt] = useState<string | null>(null);
  const drawer = openAt === (navKey ?? '');
  const setDrawer = useCallback(
    (open: boolean) => setOpenAt(open ? (navKey ?? '') : null),
    [navKey],
  );

  const toggle = useCallback(() => {
    if (!isLg) {
      setDrawer(!drawer);
      return;
    }
    setCollapsedPref((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? 'collapsed' : 'expanded');
      } catch {
        // Speicher gesperrt
      }
      return next;
    });
  }, [isLg, drawer, setDrawer]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  const collapsed = isLg ? collapsedPref : true;
  const close = () => setDrawer(false);

  return (
    <TooltipProvider delayDuration={400}>
      <PageChromeProvider titleInTopbar={false}>
        <div data-shell="ai" className="flex min-h-dvh bg-page text-fg">
          <SkipLink />
          {isMd ? (
            <aside className="sticky top-0 z-(--bar-z-nav) h-dvh shrink-0">
              {sidebar({ collapsed, toggle })}
            </aside>
          ) : null}
          <Drawer
            open={drawer}
            onOpenChange={setDrawer}
            side="left"
            title="Bar AI navigation"
            // Breite = Seitenleiste 280, Fläche `page` (DESIGN §3.3.1, §3.7.1); kein Streifen daneben.
            className="w-[min(var(--spacing-ai-sidebar),calc(100vw-48px))] bg-page [&_[data-ai-sidebar]]:w-full"
          >
            {sidebar({ collapsed: false, onNavigate: close })}
          </Drawer>
          <div className="flex min-w-0 flex-1 flex-col">
            <AiTopBar
              // Mobil ohne Gesprächstitel: Kennung „Bar AI“ wie im Seitenleisten-Kopf (§1.8, §3.3.1).
              title={title ?? (!isMd ? <PlatformMark platform="ai" size={28} /> : undefined)}
              leading={
                !isMd ? (
                  <ShellIconButton
                    icon={List}
                    label="Open navigation"
                    tone="fg"
                    aria-haspopup="dialog"
                    aria-expanded={drawer}
                    onClick={() => setDrawer(true)}
                  />
                ) : collapsed && !isLg ? (
                  <ShellIconButton icon={SidebarSimple} label="Open sidebar" onClick={toggle} />
                ) : null
              }
              actions={
                <>
                  {actions}
                  {!isMd ? (
                    <>
                      <ShellIconButton
                        icon={PencilSimpleLine}
                        label="New chat"
                        href={newChatHref}
                        tone="fg"
                      />
                      {mobileAccount}
                    </>
                  ) : null}
                </>
              }
            />
            <main
              id="main"
              tabIndex={-1}
              className={cn(
                'mx-auto w-full min-w-0 flex-1 px-4 pt-2 pb-10 outline-none md:px-6',
                width === 'wide'
                  ? 'max-w-[calc(var(--spacing-ai-wide)+48px)]'
                  : 'max-w-[calc(var(--spacing-ai-read)+48px)]',
              )}
            >
              {children}
            </main>
          </div>
          {overlays}
        </div>
      </PageChromeProvider>
    </TooltipProvider>
  );
}
